import { select as d3_select } from 'd3-selection'
import {
  curveCardinal as d3_curveCardinal,
  curveLinear as d3_curveLinear,
  line as d3_line,
} from 'd3-shape'
import { max as d3_max, min as d3_min, mean as d3_mean, extent as d3_extent } from 'd3-array'
import { easeLinear as d3_easeLinear } from 'd3-ease'
import BaseHelper from './BaseHelper'
import { scaleLinear as d3_scaleLinear } from 'd3-scale'
import {
  regressionLinear as d3_regressionLinear,
  regressionExp as d3_regressionExp,
  regressionPoly as d3_regressionPoly,
} from 'd3-regression'

export default class TrendLinesHelper extends BaseHelper {
  static configName = 'TrendLinesHelper'
  static defaultConfig = {
    index: 102,
    /*
    getItemOffset: {
      type: Function,
      default: function (d) {
        return {
          x: this.axes.x.func.scale()(d.id),
          y: this.axes.y.func.scale()(d.value),
        }
      },
    },
    */
    /**
     * {-EN-} CategoriesData must be an array of {parentId, id, value} items
     * {-ES-} CategoryData debe ser una array de elementos {parentId, id, value}
     */
    getCategoriesData: {
      type: Function,
      default: function (d) {
        return this.data
      },
    },
    /**
     * {-EN-} Set up trend lines. See [trendLines](#/developer/PropDetails#trendLines)
     * {-ES-} Configure líneas de tendencia. Ver [líneas de tendencia](#/developer/PropDetails#trendLines)
     */
    linesDict: {
      type: [Object, null],
      default: null,
      exposed: true,
    },
  }
  static init() {}
  static beforeDraw() {}
  static draw() {}

  static getXAxesIds = function () {
    if (this.config.AxisHelper.x.dimensions === null) return []
    else return ['x']
  }

  static _parsedLines = function () {
    let categoriesData = this.config.TrendLinesHelper.getCategoriesData.call(this)
    if (!this.config.TrendLinesHelper || !this.config.TrendLinesHelper.linesDict || !categoriesData)
      return []
    return Object.keys(this.config.TrendLinesHelper.linesDict).reduce((accumulator, current) => {
      let currentLine = this.config.TrendLinesHelper.linesDict[current]
      if (!!currentLine.serie) {
        accumulator.push({
          ...currentLine,
          id: current,
          data: this._calculateLineData(
            categoriesData,
            currentLine.serie,
            //: categoriesData.filter(categoryData => categoryData.parentId === currentLine.serie),
            this.config.TrendLinesHelper.linesDict[current]
          ),
        })
      }

      return accumulator
    }, [])
  }

  static _calculateLineData = function (data, serie, trendline) {
    // Remove duplicated ids
    let reducedData = data.reduce((accumulator, current) => {
      if (!accumulator[current.id]) {
        accumulator[current.id] = { ...current }
      }
      return accumulator
    }, {})
    reducedData = Object.values(reducedData)

    const serieData =
      serie === 'all' ? data : data.filter(categoryData => categoryData.parentId === serie)

    switch (trendline.type.toLowerCase()) {
      case 'max':
        const maxValue = d3_max(serieData, d => d.value)
        return reducedData.map(item => ({ ...item, value: maxValue }))
      case 'min':
        const minValue = d3_min(serieData, d => d.value)
        return reducedData.map(item => ({ ...item, value: minValue }))
      case 'average':
        const mean = d3_mean(serieData, d => d.value)
        return reducedData.map(item => ({ ...item, value: mean }))
      //For regression X axis we check if is string (categorical) in order to map by index and not by id
      case 'linear':
        let linearRegression = d3_regressionLinear()
          .x((d, index) => (typeof d.id === 'string' ? index : d.id))
          .y(d => d.value)
        return linearRegression(serieData)
      case 'exponential':
        //Check if there is a value lower than 0 in order to avoid exponential ERROR. If true, set line to 0
        let expRegression
        if (serieData.some(d => d.value < 0))
          expRegression = d3_regressionLinear()
            .x((d, index) => (typeof d.id === 'string' ? index : d.id))
            .y(() => 0)
        else
          expRegression = d3_regressionExp()
            .x((d, index) => (typeof d.id === 'string' ? index : d.id))
            .y(d => d.value)
        return expRegression(serieData)
      case 'polynomial':
        let polyRegression = d3_regressionPoly()
          .x((d, index) => (typeof d.id === 'string' ? index : d.id))
          .y(d => d.value)
          .order(parseInt(trendline.polynomialOrder))
        return polyRegression(serieData)

      default:
        return reducedData
    }
  }

  static afterDraw() {
    const trendlinesData = this._parsedLines()
    let chart = this

    let linesScale = chart.axes.y.func.scale()
    /*
    //Chart scale
    let xScale = chart.axes.x.func.scale()
    */

    const getRegressionsRange = axis => {
      let range
      if (this.getXAxesIds().length > 0) {
        if (axis === this._getAxisOrder()[0]) range = [0, this.contentSize.width]
        else {
          // CUANDO SON LOS "BARS" en las "ys" (horizonal) hay que flipear el range
          if (this.getXAxesIds().indexOf(axis) >= 0) range = [0, this.contentSize.height]
          else range = [this.contentSize.height, 0]
        }
      } else {
        range = [0, Math.min(this.contentSize.width / 2, this.contentSize.height / 2) - 20]
      }
      return range
    }

    //Specific scale for regressions in order to work independelty from chart config scale
    let xRegressionScaleLinear = d3_scaleLinear()
      .domain(
        d3_extent(
          trendlinesData.reduce((accumulator, current) => {
            accumulator.push(
              current.data.map((item, index) =>
                item.id || item.id === 0 ? (typeof item.id === 'string' ? index : item.id) : item[0]
              )
            )
            return accumulator.flat()
          }, [])
        )
      )
      .range(getRegressionsRange('x'))

    /*
    Old line using base chart scale and itemOffset
    let line = () => {
      if (this.config.TrendLinesHelper?.getItemOffset) {
        return d3_line()
          .curve(d3_curveLinear)
          .x(d => this.config.TrendLinesHelper.getItemOffset.call(this, d).x)
          .y(d => this.config.TrendLinesHelper.getItemOffset.call(this, d).y)
      }
      return d3_line()
        .curve(d3_curveLinear)
        .x(d => xScale(d.id))
        .y(d => linesScale(d.value))
    }
    line = line()
    */

    let regressionLine = () => {
      return (
        d3_line()
          .curve(d3_curveLinear)
          /*
        .x((d, index) =>
          xRegressionScaleLinear(
            d.id || d.id === 0 ? (typeof d.id === 'string' ? index : d.id) : d[0]
          )
        )
        .y(d => linesScale(d.value ? d.value : d[1]))
        */
          .x((d, index) =>
            this.config.horizontal
              ? linesScale(d.value ? d.value : d[1])
              : xRegressionScaleLinear(
                  d.id || d.id === 0 ? (typeof d.id === 'string' ? index : d.id) : d[0]
                )
          )
          .y((d, index) =>
            this.config.horizontal
              ? xRegressionScaleLinear(
                  d.id || d.id === 0 ? (typeof d.id === 'string' ? index : d.id) : d[0]
                )
              : linesScale(d.value ? d.value : d[1])
          )
      )
    }
    regressionLine = regressionLine()

    /*
    function getLineByType(type) {
      switch (type.toLowerCase()) {
        case 'max':
        case 'min':
        case 'average':
          return line
        default:
          return regressionLine
      }
    }
    */

    let trendlineRoot = d3_select(chart.$root).select('.content')

    let trendline = trendlineRoot.selectAll('.trendline-group').data(trendlinesData, d => d.id)

    const trendlineEnter = trendline
      .enter()
      .append('g')
      .attr('class', 'trendline-group')
      .append('path')
      .attr('class', 'trendline')
    trendlineEnter.attr('d', d => regressionLine(d.data))
    const pathLength = {}
    trendlineEnter.each(function (d) {
      pathLength[d.id] = d3_select(this).node().getTotalLength()
    })
    trendlineEnter
      //.attr('stroke', d => this.colorMap[d.role])
      .attr('stroke-dashoffset', d => {
        return pathLength[d.id]
      })
      .attr('stroke-dasharray', d => pathLength[d.id])
      .attr('stroke', d => (d.color ? d.color : 'red'))
      .attr('fill', 'none')
      .attr('stroke-width', d => this._getThickness(d))
      .transition()
      .ease(d3_easeLinear)
      .duration(1000)
      .attr('stroke-dashoffset', 0)

    trendlineEnter
      .transition()
      .delay(1000)
      .duration(500)
      .attr('stroke-dasharray', d => this._getStrokeDasharray(d))

    trendline.exit().remove()

    trendline
      .select('.trendline')
      .transition()
      .ease(d3_easeLinear)
      .duration(500)
      .attr('stroke', d => 'blue')
      .attr('stroke-width', d => this._getThickness(d))
      .attr('stroke', d => (d.color ? d.color : 'red'))
      .attr('fill', 'none')
      .attr('stroke-dasharray', d => this._getStrokeDasharray(d))
      .attr('d', d => regressionLine(d.data))

    const trendlineMerge = trendline.merge(trendlineEnter)

    trendlineMerge.raise()

    //trendlineMerge.attr('d', d => line(d.data))
  }
  static _getThickness = function (d) {
    if (!d.thickness) return 1
    switch (d.thickness.toLowerCase()) {
      case 'thick':
        return 6
      case 'medium':
        return 3
      default:
        return 1
    }
  }
  static _getStrokeDasharray = function (d) {
    return d.stroke === 'dashed' ? '5 3' : 'none'
  }
}
